import { useContext, useEffect } from 'react';
import { GameClientContext } from '../contexts/GameClientContext';
import GameClient from '../services/GameClient';

type UseGameExitOptions = {
    cleanupOnRouteExit?: boolean;
    clearClientOnRouteExit?: boolean;
};

const pendingRouteExitCleanups = new WeakMap<GameClient, number>();

const cancelPendingRouteExitCleanup = (client: GameClient): void => {
    const pendingCleanup = pendingRouteExitCleanups.get(client);

    if (pendingCleanup !== undefined) {
        window.clearTimeout(pendingCleanup);
        pendingRouteExitCleanups.delete(client);
    }
};

const scheduleRouteExitCleanup = (
    client: GameClient,
    clearClientOnRouteExit: boolean,
    setGameClient: (client: GameClient | null) => void
): void => {
    cancelPendingRouteExitCleanup(client);

    const cleanupId = window.setTimeout(() => {
        pendingRouteExitCleanups.delete(client);
        void client.exitGame();

        if (clearClientOnRouteExit) {
            setGameClient(null);
        }
    }, 0);

    pendingRouteExitCleanups.set(client, cleanupId);
};

export const useGameExit = (
    client: GameClient | null,
    options: UseGameExitOptions = {}
) => {
    const { setGameClient } = useContext(GameClientContext);
    const cleanupOnRouteExit = options.cleanupOnRouteExit ?? false;
    const clearClientOnRouteExit = options.clearClientOnRouteExit ?? false;

    useEffect(() => {
        const handlePageExit = () => {
            if (client) {
                client.exitGameOnUnload();
            }
        };

        window.addEventListener('pagehide', handlePageExit);
        window.addEventListener('beforeunload', handlePageExit);
        return () => {
            window.removeEventListener('pagehide', handlePageExit);
            window.removeEventListener('beforeunload', handlePageExit);
        };

    }, [client]);

    useEffect(() => {
        if (!client || !cleanupOnRouteExit) {
            return;
        }

        cancelPendingRouteExitCleanup(client);

        return () => {
            scheduleRouteExitCleanup(client, clearClientOnRouteExit, setGameClient);
        };
    }, [client, cleanupOnRouteExit, clearClientOnRouteExit, setGameClient]);

}
